import { useMemo } from "react";
import { useLocale } from "next-intl";
import {
   useRouter as useNextRouter,
   usePathname as useNextPathname
} from "next/navigation";

interface NavigationRouter {
   push(href: string, options?: { locale?: string; scroll?: boolean }): void;
   replace(href: string): void;
   back(): void;
   forward(): void;
   refresh(): void;
   prefetch(href: string): void;
}

/**
 * Returns a router instance with locale-aware navigation
 * The push method accepts an optional locale in its options:
 * - router.push('/about') -> Keeps current locale
 * - router.push('/about', { locale: 'en' }) -> Changes to English
 * - router.push('/about', { locale: 'vi' }) -> Changes to Vietnamese
 *
 * All paths are automatically prefixed with the locale:
 * - Input: "/about" with locale "en" -> Output: "/en/about"
 * - Input: "/users" with locale "vi" -> Output: "/vi/users"
 */
export function useRouter(): NavigationRouter {
   const router = useNextRouter();
   const locale = useLocale();

   return useMemo(
      () => ({
         ...router,
         push: (href: string, options?: { locale?: string; scroll?: boolean }) => {
            const targetLocale = options?.locale || locale;
            const path = href.startsWith("/") ? href : `/${href}`;
            const newPath = `/${targetLocale}${path.replace(/^\/(?=$|[?#])/, "")}`;
            router.push(newPath, { scroll: options?.scroll });
         },
         replace: (href: string) => router.replace(href),
         back: () => router.back(),
         forward: () => router.forward(),
         refresh: () => router.refresh(),
         prefetch: (href: string) => router.prefetch(href)
      }),
      [router, locale]
   );
}

/**
 * Returns the current pathname without the locale prefix
 * For example:
 * - Input: "/en/about" -> Output: "/about"
 * - Input: "/vi/users/123" -> Output: "/users/123"
 * - Input: "/en" or "/vi" -> Output: "/"
 */
export function usePathname(): string | null {
   const pathname = useNextPathname();
   if (!pathname) return pathname;

   // Remove the locale prefix (e.g., /en/ or /vi/) from the pathname
   const segments = pathname.split("/");
   return segments.length > 2 ? `/${segments.slice(2).join("/")}` : "/";
}
